import React, { useMemo, useState } from 'react'
import { Form } from 'antd'
import { OneModal as Modal } from '@yy/one-ui'
import type { FormInstance, FormProps } from 'antd/es/form'
import { useTranslation } from 'react-i18next'
import type { ModalProps } from 'antd/es/modal'
import { useForm } from 'antd/lib/form/Form'

export interface FormModalProps<V = Record<string, any>> extends ModalProps, Omit<FormProps<V>, 'title'> {
  children: React.ReactNode
  /** 确定放弃修改 */
  onGiveUpOk?: () => void
  /** 提交表单的回调事件 */
  onFinish?: (value: V) => Promise<void>
  /** 提交的回调事件 */
  onSuccess?: () => void

  beforeSubmit?: (form: FormInstance<V>) => Promise<void>
}
const FormModal = <V,>(props: FormModalProps<V>) => {
  const { t } = useTranslation()
  const {
    children,
    visible,
    closable = false,
    onCancel,
    title,
    form: outerForm,
    centered = true,
    initialValues,
    onFinish,
    onSuccess,
    okText = t('common.confirm'),
    cancelText = t('common.cancel'),
    okButtonProps,
    bodyStyle,
    beforeSubmit,
    ...rest
  } = props
  const [loading, setLoading] = useState<boolean>(false)
  const [innerForm] = useForm()

  const form = useMemo(() => {
    return outerForm ?? innerForm
  }, [outerForm, innerForm])
  const handleCancel = (e: any) => {
    onCancel?.(e)
  }
  /**
   * 表单提交
   */
  const handleFinish = (value: V) => {
    onFinish?.(value)
      .then(() => {
        onSuccess?.()
      })
      .finally(() => {
        setLoading(false)
      })
  }

  /**
   * 表单重置
   */
  const resetComfirm = (e: any) => {
    form?.resetFields()
    handleCancel(e)
  }

  return (
    <Modal
      bodyStyle={bodyStyle}
      closable={closable}
      title={title}
      visible={visible}
      centered={centered}
      onOk={async () => {
        setLoading(true)
        try {
          await beforeSubmit?.(form)
          form?.submit()
        } catch (e) {
          setLoading(false)
        }
      }}
      onCancel={resetComfirm}
      confirmLoading={loading}
      okText={okText}
      okButtonProps={okButtonProps}
      cancelText={cancelText}
    >
      <Form
        {...rest}
        form={form}
        initialValues={initialValues}
        onFinish={handleFinish}
        onFinishFailed={async (errorInfo) => {
          await rest.onFinishFailed?.(errorInfo)
          setLoading(false)
        }}
      >
        {children}
      </Form>
    </Modal>
  )
}

export default FormModal
